import {
  validatePhotoFileNames,
  validateDocFileNames,
  toLowerCaseCustom,
  fixOldDocStringMigrationProblem,
  processTypes,
  toValidPfDate,
} from "../../utils"

const getDateActionedDate = (data: Record<string, any>) => {
  if (data.dateActioned) {
    return data.dateActioned
  }
  if (data.discActionIssuedDate) {
    return data.discActionIssuedDate
  }
  return ""
}

const getOffence = (data: Record<string, any>) => {
  if (data.offence) {
    return data.offence
  }
  if (toLowerCaseCustom(data.action) === processTypes.terminate && data.reasonForTermination) {
    return data.reasonForTermination
  }
  return ""
}
class DisciplineRecord {
  uid: string
  rawData: { process: string } & Record<string, any>
  photos: string[]
  docs: string[]
  logs: Record<string, any>[]
  uiData: Record<string, any>
  previousRawData: Record<string, any>

  constructor({
    uid = "",
    rawData = {} as Record<string, any>,
    photos = [],
    docs = [],
    logs = [],
    uiData = {} as Record<string, any>,
    previousRawData = {} as Record<string, any>,
  }) {
    const currentDate = toValidPfDate(Date.now())
    this.uid = uid
    this.rawData = {
      process: rawData.process || processTypes.discipline,
      association: rawData.association || "",
      division: rawData.division || "",
      jobType: rawData.jobType || "",
      jobSubType: rawData.jobSubType || "",
      action: rawData.action || "",
      offence: getOffence(rawData),
      expiry: rawData.expiry || "",
      discOffenceDate: rawData.discOffenceDate || currentDate,
      discActionIssuedDate: rawData.discActionIssuedDate || currentDate,
      discOffenceDescription: rawData.discOffenceDescription || "",
      employeeResponse: rawData.employeeResponse || "",
      userName: rawData.userName || "",
      userSurname: rawData.userSurname || "",
      witnessName: rawData.witnessName || "",
      performanceDate: rawData.performanceDate || "",
      dateActioned: getDateActionedDate(rawData),
    }
    this.photos = photos
    this.docs = docs
    this.logs = logs
    this.previousRawData = previousRawData
    this.uiData = {
      isPaused: uiData.isPaused || false,
      isPrescribedAction: uiData.isPrescribedAction || false,
      flowType: "actionFlow",
    }
  }

  addTimestamp = (dateActioned = `${new Date().getTime()}`): string => {
    this.rawData.dateActioned = dateActioned
    return dateActioned
  }

  getAssociation = () => {
    return this.rawData.association
  }

  convertToJson = () => {
    return {
      uid: this.getUid(),
      rawData: this.getRawData(),
      photos: this.getPhotoFileNames(),
      docs: this.getDocFileNames(),
      logs: this.logs,
      previousRawData: this.previousRawData,
    }
  }

  getRawData = () => {
    return this.rawData
  }

  getRawDataValue = (targeyKey: string) => {
    if (this.rawData.hasOwnProperty(targeyKey)) {
      return this.rawData[targeyKey]
    }
    return ""
  }

  getUiData = () => {
    return this.uiData
  }

  getPhotoFileNames = () => {
    if (!validatePhotoFileNames(this.photos)) {
      return []
    }
    return this.photos
  }

  getDocFileNames = () => {
    if (!validateDocFileNames(this.docs)) {
      return []
    }
    return this.docs.map((docFileName) => fixOldDocStringMigrationProblem(docFileName))
  }

  getAction = () => {
    return this.rawData.action
  }

  getProcess = () => {
    return this.rawData.process
  }

  getOffence = () => {
    return this.rawData.offence
  }

  getUid = () => {
    return this.uid
  }

  isPrescribedAction = () => {
    return this.getUiData().isPrescribedAction
  }

  setUid = (uid: string) => {
    this.uid = uid
  }

  checkIfActioned = () => {
    return this.uid ? true : false
  }

  upsertRawData = (newData: Record<string, any> = {}, preventOverwrite = false) => {
    Object.keys(newData).forEach((key) => {
      if (this.rawData.hasOwnProperty(key) && preventOverwrite) {
        return
      }
      this.rawData[key] = newData[key]
    })
  }

  upsertPreviousRawData = (previousRawData: Record<string, any> = {}) => {
    this.previousRawData = previousRawData
  }

  upsertUiData = (newData: Record<string, any> = {}) => {
    Object.keys(newData).forEach((key) => {
      this.uiData[key] = newData[key]
    })
  }

  upsertDocs = (newDocs: string[] = []) => {
    this.docs.unshift(...newDocs)
  }

  upsertPhotos = (newPhotos: string[] = []) => {
    this.photos.unshift(...newPhotos)
  }

  upsertLogEntries = (newLogEntries: Record<string, any>[] = []) => {
    return this.logs.push(...newLogEntries)
  }
}

export { DisciplineRecord }
